const STORAGE_KEY = 'cnstrc_user_segments';
const BASE_SEGMENTS = ['web'];

const readStored = () => {
  if (typeof sessionStorage === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeStored = (map) => {
  if (typeof sessionStorage === 'undefined' || !map) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // sessionStorage unavailable / quota exceeded — segments just won't persist
  }
};

/**
 * Resolve Constructor user segments from the current URL plus an additive
 * per-key history persisted in sessionStorage (cleared automatically when the
 * browser tab closes).
 *
 * For each tracked key, every distinct value seen during the session is kept
 * in insertion order. The returned segment list is the flat union of all
 * values across all tracked keys — so e.g. `?source=email` followed later by
 * `?source=product` results in both `email` and `product` being passed to
 * Constructor.
 *
 * @param {Array<string>|null|undefined} trackedKeys - keys from the Shopify metafield
 * @param {string} [href=window.location.href]
 * @returns {Array<string>} accumulated segment values across all tracked keys
 */
export const getUserSegments = (trackedKeys, href = window.location.href) => {
  if (!Array.isArray(trackedKeys) || trackedKeys.length === 0) return [];

  const stored = readStored();
  let changed = false;

  try {
    const params = new URL(href).searchParams;
    trackedKeys.forEach((key) => {
      if (typeof key !== 'string' || !key) return;
      if (!params.has(key)) return;
      const value = params.get(key);
      if (!value) return;

      const history = Array.isArray(stored[key]) ? stored[key] : [];
      if (!history.includes(value)) {
        history.push(value);
        stored[key] = history;
        changed = true;
      }
    });
  } catch (error) {
    // malformed URL — fall back to stored values only
    console.error(error);
  }

  if (changed) writeStored(stored);

  // Flatten all per-key histories into a single de-duplicated segment list.
  const seen = new Set();
  const segments = [];
  trackedKeys.forEach((key) => {
    if (typeof key !== 'string') return;
    const history = stored[key];
    if (!Array.isArray(history)) return;
    history.forEach((value) => {
      if (!seen.has(value)) {
        seen.add(value);
        segments.push(value);
      }
    });
  });

  return segments;
};

/**
 * Initializes `window.cnstrc.userSegments` and keeps it in sync as new
 * tracking params arrive via the URL — both on full page loads (module
 * init) and on in-session history changes (popstate, pushState, replaceState).
 *
 * The emitted list is BASE_SEGMENTS, then the selected Shopify market as a lowercase
 * ISO 2 character code, then the accumulated tracking param values. The market is read
 * fresh on every sync and never persisted, so switching country replaces the previous
 * code rather than adding to it. Anything that is not a two-letter code — including a
 * missing `window.Shopify` — falls back to `us`.
 *
 * The same array instance is mutated in place so callers that captured the
 * reference (e.g. the Constructor SDK) continue to see updates.
 *
 * @returns {Array<string>} the live, mutating segments array on window.cnstrc
 */
export const initUserSegments = () => {
  if (typeof window === 'undefined') return [];
  window.cnstrc = window.cnstrc || {};
  if (!Array.isArray(window.cnstrc.userSegments)) window.cnstrc.userSegments = [];
  const segments = window.cnstrc.userSegments;

  const sync = () => {
    const market = (window.Shopify?.country || '').toLowerCase();
    const country = /^[a-z]{2}$/.test(market) ? market : 'us';
    const fresh = [
      ...new Set([...BASE_SEGMENTS, country, ...getUserSegments(window.cnstrc?.userSegmentTrackingParameters)]),
    ];
    segments.length = 0;
    segments.push(...fresh);
  };

  sync();

  if (!window.__cnstrcSegmentsListening) {
    window.__cnstrcSegmentsListening = true;

    window.addEventListener('popstate', sync);

    // pushState / replaceState don't fire any event, so wrap them.
    ['pushState', 'replaceState'].forEach((method) => {
      const original = history[method];
      if (typeof original !== 'function') return;
      history[method] = function patched(...args) {
        const result = original.apply(this, args);
        sync();
        return result;
      };
    });
  }

  return segments;
};
